<template>
  <div class="form-container">
    <h1>ユーザ新規作成</h1>
    <form @submit.prevent="handleSubmit">
      <div class="mb-3">
        <label for="userName" class="form-label">ユーザ名 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="user.name" type="text" placeholder="ユーザ名" class="form-control" id="userName" required>
        <div v-if="errorMessages?.name" class="alert alert-danger mt-2">{{ errorMessages?.name[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="userEmail" class="form-label">メールアドレス <span class="badge text-bg-danger">必須</span></label>
        <input v-model="user.email" type="email" placeholder="メールアドレス" class="form-control" id="userEmail" required>
        <div v-if="errorMessages?.email" class="alert alert-danger mt-2">{{ errorMessages?.email[0] }}</div>
      </div>

      <div class="mb-3">
        <label class="form-label">システム別権限 <span class="badge text-bg-danger">必須</span></label>
        <div
          v-for="clientSystem in clientSystems"
          :key="clientSystem.key"
          class="mb-2"
        >
          <label :for="`role-${clientSystem.key}`" class="form-label">{{ clientSystem.label }}</label>
          <select
            :id="`role-${clientSystem.key}`"
            v-model="user.roles_by_system[clientSystem.key]"
            class="form-control"
            required
          >
            <option
              v-for="roleOption in roleOptions"
              :key="`${clientSystem.key}-${roleOption.value}`"
              :value="String(roleOption.value)"
            >
              {{ roleOption.label }}
            </option>
          </select>
        </div>
        <div v-if="errorMessages?.roles_by_system" class="alert alert-danger mt-2">
          {{ errorMessages?.roles_by_system[0] }}
        </div>
      </div>

      <div class="mb-3">
        <label for="userPassword" class="form-label">パスワード <span class="badge text-bg-danger">必須</span></label>
        <input v-model="user.password" type="password" placeholder="パスワード" class="form-control" id="userPassword" required>
        <div v-if="errorMessages?.password" class="alert alert-danger mt-2">{{ errorMessages?.password[0] }}</div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-primary">登録</button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { createUser, getUserRoleCatalog } from '@/api/axios';

export default {
  setup() {
    const title = ref('ユーザ新規作成');
    const store = useStore();
    const router = useRouter();

    const user = ref({
      name: '',
      email: '',
      role: '',
      roles_by_system: {},
      password: ''
    });

    const errorMessages = ref({});
    const clientSystems = ref([]);
    const roleOptions = ref([]);
    const currentClientSystem = ref(null);

    watchEffect(() => {
      document.title = title.value;
    });

    const fetchRoleCatalog = async () => {
      const response = await getUserRoleCatalog();
      clientSystems.value = response.data.client_systems || [];
      roleOptions.value = response.data.role_options || [];
      currentClientSystem.value = response.data.current_client_system || clientSystems.value[0]?.key;

      const defaultRole = String(roleOptions.value.find((option) => option.value === 1)?.value ?? roleOptions.value[0]?.value ?? 1);
      user.value.role = defaultRole;
      user.value.roles_by_system = clientSystems.value.reduce((acc, system) => {
        acc[system.key] = defaultRole;
        return acc;
      }, {});
    };

    const buildRolesBySystemPayload = () => {
      return clientSystems.value.reduce((acc, system) => {
        const parsedRole = Number(user.value.roles_by_system?.[system.key]);
        if (!Number.isNaN(parsedRole)) {
          acc[system.key] = parsedRole;
        }
        return acc;
      }, {});
    };

    const handleSubmit = async () => {
      try {
        const rolesBySystem = buildRolesBySystemPayload();
        const roleForCurrentSystem = rolesBySystem[currentClientSystem.value] ?? Number(user.value.role);

        await createUser({
          ...user.value,
          role: roleForCurrentSystem,
          roles_by_system: rolesBySystem
        });

        store.commit('setFlashMessage', { message: 'ユーザー作成完了！', type: 'success' });
        router.push('/users');
      } catch (error) {
        errorMessages.value = error.response?.data || {};
        store.commit('setFlashMessage', { message: 'ユーザー作成失敗！', type: 'danger' });
      }
    };

    onMounted(async () => {
      await fetchRoleCatalog();
    });

    return {
      title,
      user,
      errorMessages,
      clientSystems,
      roleOptions,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.form-container {
  width: 70%;
  padding: 20px;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

input[type="text"],
input[type="email"],
input[type="password"] {
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.btn-area{
  text-align: center;
}
</style>
